/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  UserPlus, Briefcase, FileText, ShieldCheck, CheckCircle2, Save,
  X, Plus, Trash2, Edit2, Star, Upload, Loader2, AlertTriangle,
  ChevronRight, Sparkles, MessageSquare, HelpCircle, Copy,
} from 'lucide-react';
import CountryCombobox from '../components/CountryCombobox';

const STEPS = [
  { key: 'client_information', label: 'Client Information', sub: 'Tell us about your client' },
  { key: 'stakeholders',       label: 'Stakeholders',       sub: 'People in the loop' },
  { key: 'project_details',    label: 'Project Details',    sub: 'What we are delivering' },
  { key: 'creative_brief',     label: 'Creative Brief',     sub: 'Vision & requirements' },
  { key: 'access_permissions', label: 'Access & Permissions', sub: 'Who sees what' },
  { key: 'review',             label: 'Review & Confirm',   sub: 'Final step' },
];

const initialPayload = {
  client_information: {
    client_type: 'company',
    company_name: '', industry: '', website: '', business_description: '',
    country: 'India', timezone: 'Asia/Kolkata',
    working_hours_start: '10:00', working_hours_end: '19:00',
    primary_language: 'English', currency: 'INR',
    preferred_channel: 'email', update_frequency: 'weekly',
    client_category: 'Production House',
    billing_type: 'fixed', gst_tax_id: '', billing_email: '',
    nda_status: 'pending',
  },
  stakeholders: [],
  project_details: {
    name: '', deadline: '', description: '',
    type: 'series', fps: 24, resolution: '1920x1080',
    duration_min: 22, currency: 'INR', budget: 0,
  },
  creative_brief: { files: [], notes: '' },
  access_permissions: { grants: [] },
};

export default function ClientCreatePage() {
  const { api, user } = useAuth();
  const [step, setStep] = useState(0);
  const [payload, setPayload] = useState(initialPayload);
  const [draftId, setDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [progress, setProgress] = useState({ percent: 0, steps_completed: [], missing_steps: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const isAllowed = ['master_admin', 'production_manager', 'hr_manager'].includes(user?.role);

  // Auto-save (debounced) on payload change
  useEffect(() => {
    if (!isAllowed) return;
    const id = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await api.post('/admin/clients/draft', { draft_id: draftId, payload });
        setDraftId(r.data.draft_id);
        setProgress(r.data.progress);
        setSavedAt(new Date());
      } catch {} finally { setSaving(false); }
    }, 1200);
    return () => clearTimeout(id);
  }, [payload]);

  const set = (key, patch) => setPayload(p => ({ ...p, [key]: { ...p[key], ...patch } }));
  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const finalize = async () => {
    setSubmitting(true); setError('');
    try {
      const r = await api.post('/admin/clients/finalize', {
        draft_id: draftId, payload, send_welcome_email: true,
      });
      setResult(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Onboarding failed'); }
    finally { setSubmitting(false); }
  };

  if (!isAllowed) {
    return <div className="text-zinc-500 text-sm py-8 text-center">Admin / Producer / HR Manager only.</div>;
  }

  if (result) return <FinalSuccess result={result} onReset={() => { setResult(null); setStep(0); setPayload(initialPayload); setDraftId(null); }} />;

  return (
    <div className="space-y-4" data-testid="client-onboarding-wizard">
      {/* Top bar with stepper */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">New Client Onboarding</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Structured 6-step intake — stays saved as you type so you can pick up where you left off.</p>
        </div>
        <div className="flex items-center gap-2">
          <DraftSavedHint saving={saving} savedAt={savedAt} />
          <button onClick={finalize} disabled={submitting || progress.percent < 60}
            data-testid="create-client-top"
            className="px-4 py-2 bg-violet-500 hover:bg-violet-400 disabled:opacity-30 rounded-sm text-xs text-white font-medium flex items-center gap-1.5">
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Create Client
          </button>
        </div>
      </div>

      {/* Stepper */}
      <Stepper step={step} setStep={setStep} progress={progress} />

      {/* Layout: 2 columns - left main, right summary */}
      <div className="grid grid-cols-[1fr_18rem] gap-4">
        <div className="space-y-3" data-testid="wizard-main-pane">
          {step === 0 && <StepClientInfo payload={payload.client_information} set={(p) => set('client_information', p)} />}
          {step === 1 && <StepStakeholders payload={payload.stakeholders} set={(arr) => setPayload(p => ({ ...p, stakeholders: arr }))} />}
          {step === 2 && <StepProjectDetails payload={payload.project_details} set={(p) => set('project_details', p)} />}
          {step === 3 && <StepCreativeBrief payload={payload.creative_brief} set={(p) => set('creative_brief', p)} api={api} />}
          {step === 4 && <StepAccessPermissions payload={payload.access_permissions} setRoot={setPayload} stakeholders={payload.stakeholders} />}
          {step === 5 && <StepReview payload={payload} progress={progress} onCommit={finalize} submitting={submitting} error={error} />}

          {/* Footer nav */}
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => window.history.back()} data-testid="wizard-cancel"
              className="text-[11px] text-zinc-500 hover:text-zinc-300 px-3 py-2">Cancel</button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button onClick={back} data-testid="wizard-back"
                  className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 hover:border-zinc-700">
                  ← Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button onClick={next} data-testid="wizard-next"
                  className="px-4 py-2 bg-violet-500 hover:bg-violet-400 rounded-sm text-xs text-white font-medium flex items-center gap-1.5">
                  Next Step <ChevronRight className="w-3 h-3" />
                </button>
              ) : (
                <button onClick={finalize} disabled={submitting} data-testid="wizard-finalize"
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded-sm text-xs text-white font-medium flex items-center gap-1.5">
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  Create Client
                </button>
              )}
            </div>
          </div>
          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-sm p-2.5 text-[11px] text-rose-300 flex items-start gap-2" data-testid="wizard-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {error}
            </div>
          )}
        </div>

        {/* Right summary */}
        <SummaryRail payload={payload} progress={progress} />
      </div>
    </div>
  );
}

// ───────────────────────── Stepper ─────────────────────────
const Stepper = ({ step, setStep, progress }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid="wizard-stepper">
    <div className="flex items-center gap-2 overflow-x-auto">
      {STEPS.map((s, i) => {
        const isActive = i === step;
        const isComplete = progress.steps_completed.includes(s.key);
        return (
          <React.Fragment key={s.key}>
            <button onClick={() => setStep(i)} data-testid={`step-${i}-${s.key}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-left whitespace-nowrap ${isActive ? 'bg-violet-500/15' : ''}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium ${
                isActive ? 'bg-violet-500 text-white' : isComplete ? 'bg-emerald-500/30 text-emerald-300' : 'bg-zinc-800 text-zinc-500'
              }`}>{isComplete ? '✓' : i + 1}</span>
              <div className="leading-tight">
                <p className={`text-[11px] font-medium ${isActive ? 'text-violet-300' : isComplete ? 'text-emerald-300' : 'text-zinc-400'}`}>{s.label}</p>
                <p className="text-[9px] text-zinc-600">{s.sub}</p>
              </div>
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-700 flex-shrink-0" />}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

// ───────────────────────── Step 1 — Client Info ─────────────────────────
const TYPES = [
  { v: 'company', l: 'Company / Studio' },
  { v: 'individual', l: 'Individual' },
  { v: 'agency', l: 'Agency' },
];
const StepClientInfo = ({ payload, set }) => (
  <Card title="1. Client Information" sub="Tell us about your client organization" testid="step-client-info">
    <div>
      <Label>Client Type *</Label>
      <div className="flex gap-1.5 mt-1">
        {TYPES.map(t => (
          <button key={t.v} onClick={() => set({ client_type: t.v })} data-testid={`client-type-${t.v}`}
            className={`px-3 py-1.5 rounded-sm text-[11px] border ${payload.client_type === t.v ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'bg-zinc-900 border-zinc-800 text-zinc-400'}`}>
            {t.l}
          </button>
        ))}
      </div>
    </div>
    <Grid>
      <Input label="Company / Studio Name *" v={payload.company_name} on={(v) => set({ company_name: v })} testid="ci-company" />
      <Select label="Industry" v={payload.industry} on={(v) => set({ industry: v })} testid="ci-industry"
        options={['Film Production', 'TV / Streaming', 'Advertising', 'Gaming', 'Education', 'EdTech', 'Other']} />
      <Input label="Website" v={payload.website} on={(v) => set({ website: v })} testid="ci-website" />
      <CountryCombobox value={payload.country} onChange={(v) => set({ country: v })} testid="ci-country" />
    </Grid>
    <TextArea label="Business Description (optional)" v={payload.business_description} on={(v) => set({ business_description: v })} testid="ci-desc" rows={2} />

    <div className="pt-3 mt-3 border-t border-zinc-800">
      <p className="text-[11px] font-medium text-zinc-300 mb-2 flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> Client Context</p>
      <Grid>
        <Input label="Timezone *" v={payload.timezone} on={(v) => set({ timezone: v })} testid="ci-tz" />
        <Input label="Working Hours Start" type="time" v={payload.working_hours_start} on={(v) => set({ working_hours_start: v })} testid="ci-wh-start" />
        <Input label="Working Hours End" type="time" v={payload.working_hours_end} on={(v) => set({ working_hours_end: v })} testid="ci-wh-end" />
        <Input label="Primary Language" v={payload.primary_language} on={(v) => set({ primary_language: v })} testid="ci-lang" />
        <Select label="Currency *" v={payload.currency} on={(v) => set({ currency: v })} testid="ci-currency"
          options={['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD']} />
        <Select label="Preferred Channel" v={payload.preferred_channel} on={(v) => set({ preferred_channel: v })} testid="ci-channel"
          options={['email', 'slack', 'whatsapp', 'teams', 'phone']} />
        <Select label="Update Frequency" v={payload.update_frequency} on={(v) => set({ update_frequency: v })} testid="ci-freq"
          options={['daily', 'weekly', 'biweekly', 'monthly', 'milestone-based']} />
        <Select label="Client Category" v={payload.client_category} on={(v) => set({ client_category: v })} testid="ci-cat"
          options={['Production House', 'Streaming Platform', 'Direct Brand', 'Ad Agency', 'Studio', 'Indie Filmmaker']} />
        <Select label="Billing Type" v={payload.billing_type} on={(v) => set({ billing_type: v })} testid="ci-billing"
          options={['fixed', 'time_and_material', 'retainer', 'milestone']} />
        <Input label="GST / Tax ID" v={payload.gst_tax_id} on={(v) => set({ gst_tax_id: v })} testid="ci-gst" />
        <Input label="Billing Email" v={payload.billing_email} on={(v) => set({ billing_email: v })} testid="ci-billing-email" />
        <Select label="NDA / Contract Status" v={payload.nda_status} on={(v) => set({ nda_status: v })} testid="ci-nda"
          options={['pending', 'sent', 'signed', 'not_required']} />
      </Grid>
    </div>
  </Card>
);

// ───────────────────────── Step 2 — Stakeholders ─────────────────────────
const TAG_OPTIONS = [
  { v: 'all_emails', l: 'All Emails', c: 'bg-violet-500/15 text-violet-300' },
  { v: 'primary_contact', l: 'Primary Contact', c: 'bg-emerald-500/15 text-emerald-300' },
  { v: 'approvals', l: 'Approvals', c: 'bg-amber-500/15 text-amber-300' },
  { v: 'billing_only', l: 'Billing Only', c: 'bg-orange-500/15 text-orange-300' },
  { v: 'updates', l: 'Updates', c: 'bg-cyan-500/15 text-cyan-300' },
  { v: 'view_only', l: 'View Only', c: 'bg-zinc-700 text-zinc-300' },
];
const ROLES = [
  { v: 'decision_maker', l: 'Decision Maker', c: 'text-violet-300', bg: 'bg-violet-500/15' },
  { v: 'approver', l: 'Approver', c: 'text-blue-300', bg: 'bg-blue-500/15' },
  { v: 'reviewer', l: 'Reviewer', c: 'text-cyan-300', bg: 'bg-cyan-500/15' },
  { v: 'finance', l: 'Finance', c: 'text-orange-300', bg: 'bg-orange-500/15' },
  { v: 'marketing', l: 'Marketing', c: 'text-pink-300', bg: 'bg-pink-500/15' },
  { v: 'watcher', l: 'Watcher', c: 'text-zinc-300', bg: 'bg-zinc-700' },
];
const StepStakeholders = ({ payload, set }) => {
  const [editing, setEditing] = useState(null);
  const blank = { id: '', name: '', email: '', phone: '', title: '', role_in_project: 'reviewer', tags: [], is_primary: false };
  const setPrimary = (id) => set(payload.map(s => ({ ...s, is_primary: s.id === id })));
  const remove = (id) => set(payload.filter(s => s.id !== id));
  const upsert = (s) => {
    if (s.id) set(payload.map(x => x.id === s.id ? s : x));
    else {
      const nid = `s_${Date.now()}`;
      const isFirst = payload.length === 0;
      set([...payload, { ...s, id: nid, is_primary: isFirst || s.is_primary }]);
    }
    setEditing(null);
  };
  return (
    <Card title="2. Stakeholders" sub="Add all the people who should be in the communication loop" testid="step-stakeholders"
      action={
        <button onClick={() => setEditing({ ...blank })} data-testid="add-person-btn"
          className="px-3 py-1.5 bg-violet-500 hover:bg-violet-400 rounded-sm text-[11px] text-white font-medium flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> Add Person
        </button>
      }>
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden" data-testid="stakeholder-table">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-zinc-800 text-[9px] text-zinc-500 uppercase">
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-2 py-2">Email</th>
              <th className="text-left px-2 py-2">Title</th>
              <th className="text-left px-2 py-2 w-32">Role</th>
              <th className="text-left px-2 py-2">Tags</th>
              <th className="text-center px-2 py-2 w-16">Primary</th>
              <th className="text-right px-3 py-2 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/40">
            {payload.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-zinc-600">No stakeholders yet — click "Add Person".</td></tr>
            )}
            {payload.map(s => {
              const role = ROLES.find(r => r.v === s.role_in_project) || ROLES[2];
              return (
                <tr key={s.id} data-testid={`stakeholder-row-${s.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-[9px] text-white">
                        {s.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <span className="text-zinc-200">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-zinc-400">{s.email}</td>
                  <td className="px-2 py-2 text-zinc-500">{s.title}</td>
                  <td className="px-2 py-2">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase ${role.bg} ${role.c}`}>{role.l}</span>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(s.tags || []).map(t => {
                        const opt = TAG_OPTIONS.find(o => o.v === t);
                        return <span key={t} className={`text-[8px] px-1.5 py-0.5 rounded ${opt?.c || 'bg-zinc-700 text-zinc-300'}`}>{opt?.l || t}</span>;
                      })}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button onClick={() => setPrimary(s.id)} data-testid={`primary-${s.id}`}>
                      <Star className={`w-3.5 h-3.5 ${s.is_primary ? 'fill-amber-400 text-amber-400' : 'text-zinc-700 hover:text-zinc-500'}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(s)} className="text-zinc-500 hover:text-cyan-400 mr-2" data-testid={`edit-${s.id}`}><Edit2 className="w-3 h-3" /></button>
                    <button onClick={() => remove(s.id)} className="text-zinc-500 hover:text-rose-400" data-testid={`del-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-zinc-500 mt-2">⚐ Mark exactly one stakeholder as <span className="text-amber-300">Primary</span> — only the Primary gets a login by default; others are notification-only contacts. You can promote a contact to a login later.</p>
      {editing && <PersonModal initial={editing} onSave={upsert} onClose={() => setEditing(null)} />}
    </Card>
  );
};

const PersonModal = ({ initial, onSave, onClose }) => {
  const [s, setS] = useState({ tags: [], ...initial });
  const ok = s.name && s.email;
  const toggleTag = (t) => setS(p => ({ ...p, tags: (p.tags || []).includes(t) ? p.tags.filter(x => x !== t) : [...(p.tags || []), t] }));
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 w-[36rem]" onClick={e => e.stopPropagation()} data-testid="person-modal">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-sm font-heading font-semibold text-white">{initial.id ? 'Edit Stakeholder' : 'Add Stakeholder'}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <Grid>
          <Input label="Full Name *" v={s.name} on={(v) => setS({ ...s, name: v })} testid="pm-name" />
          <Input label="Email *" v={s.email} on={(v) => setS({ ...s, email: v })} testid="pm-email" />
          <Input label="Phone" v={s.phone} on={(v) => setS({ ...s, phone: v })} testid="pm-phone" />
          <Input label="Title / Designation" v={s.title} on={(v) => setS({ ...s, title: v })} testid="pm-title" />
        </Grid>
        <div className="mt-3">
          <Label>Role in Project *</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {ROLES.map(r => (
              <button key={r.v} onClick={() => setS({ ...s, role_in_project: r.v })} data-testid={`pm-role-${r.v}`}
                className={`px-2 py-1 text-[10px] rounded border ${s.role_in_project === r.v ? `${r.bg} border-${r.c} ${r.c}` : 'bg-zinc-950 border-zinc-800 text-zinc-500'}`}>
                {r.l}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3">
          <Label>Tags</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {TAG_OPTIONS.map(t => (
              <button key={t.v} onClick={() => toggleTag(t.v)} data-testid={`pm-tag-${t.v}`}
                className={`px-2 py-1 text-[10px] rounded ${(s.tags || []).includes(t.v) ? t.c : 'bg-zinc-800 text-zinc-500'}`}>
                {t.l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 text-zinc-500 hover:text-white">Cancel</button>
          <button onClick={() => ok && onSave(s)} disabled={!ok} data-testid="pm-save"
            className="text-[11px] px-3 py-1.5 bg-violet-500 disabled:opacity-30 hover:bg-violet-400 rounded-sm text-white font-medium">Save</button>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────── Step 3 — Project Details ─────────────────────────
const StepProjectDetails = ({ payload, set }) => (
  <Card title="3. Project Details" sub="Locks the deliverable shape — fps, resolution, type, budget" testid="step-project-details">
    <Grid>
      <Input label="Project Name *" v={payload.name} on={(v) => set({ name: v })} testid="pd-name" />
      <Input label="Deadline *" type="date" v={payload.deadline} on={(v) => set({ deadline: v })} testid="pd-deadline" />
    </Grid>
    <TextArea label="Description" v={payload.description} on={(v) => set({ description: v })} testid="pd-desc" rows={3} />
    <Grid cols={4}>
      <Select label="Type" v={payload.type} on={(v) => set({ type: v })} testid="pd-type"
        options={['series', 'feature', 'short', 'commercial', 'music_video', 'explainer', 'tvc', 'game_cinematic']} />
      <Select label="FPS" v={String(payload.fps)} on={(v) => set({ fps: Number(v) })} testid="pd-fps"
        options={['12', '24', '25', '30', '60']} />
      <Select label="Resolution" v={payload.resolution} on={(v) => set({ resolution: v })} testid="pd-res"
        options={['1280x720', '1920x1080', '2048x1080', '3840x2160', '4096x2160']} />
      <Input label="Duration (min)" type="number" v={payload.duration_min} on={(v) => set({ duration_min: Number(v) })} testid="pd-duration" />
    </Grid>
    <Grid>
      <Select label="Currency" v={payload.currency} on={(v) => set({ currency: v })} testid="pd-currency"
        options={['INR', 'USD', 'EUR', 'GBP', 'AED']} />
      <Input label="Budget" type="number" v={payload.budget} on={(v) => set({ budget: Number(v) })} testid="pd-budget" />
    </Grid>
  </Card>
);

// ───────────────────────── Step 4 — Creative Brief ─────────────────────────
const StepCreativeBrief = ({ payload, set, api }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setErr('');
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        const { data } = await api.post('/admin/clients/brief-upload', fd);
        uploaded.push(data);
      }
      set({ files: [...(payload.files || []), ...uploaded] });
    } catch (e) { setErr(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const remove = (id) => set({ files: payload.files.filter(f => f.id !== id) });
  return (
    <Card title="4. Creative Brief" sub="Vision & requirements — upload xlsx, pdf, doc, ppt, csv, or txt" testid="step-creative-brief">
      <input ref={fileRef} type="file" multiple accept=".xlsx,.xls,.pdf,.doc,.docx,.pptx,.csv,.txt" onChange={onPick} className="hidden" data-testid="cb-file-input" />
      <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="cb-upload-btn"
        className="w-full px-4 py-6 border-2 border-dashed border-zinc-700 rounded-sm bg-zinc-900/40 hover:border-violet-500/50 hover:bg-violet-500/5 disabled:opacity-50 flex flex-col items-center gap-2 transition-all">
        {busy ? <Loader2 className="w-5 h-5 animate-spin text-violet-400" /> : <Upload className="w-5 h-5 text-zinc-400" />}
        <span className="text-xs text-zinc-300">{busy ? 'Uploading…' : 'Click to upload brief documents'}</span>
        <span className="text-[10px] text-zinc-600">.xlsx · .pdf · .doc / .docx · .pptx · .csv · .txt (max 25 MB each)</span>
      </button>
      {err && <p className="text-[11px] text-rose-400 mt-2">{err}</p>}
      <div className="space-y-1.5 mt-3" data-testid="cb-file-list">
        {(payload.files || []).map(f => (
          <div key={f.id} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-[11px]" data-testid={`cb-file-${f.id}`}>
            <FileText className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-zinc-200 flex-1 truncate">{f.filename}</span>
            <span className="text-[9px] text-zinc-600 uppercase">{f.kind}</span>
            <span className="text-[9px] text-zinc-600">{Math.round(f.size / 1024)} KB</span>
            <button onClick={() => remove(f.id)} className="text-zinc-500 hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <TextArea label="Additional Notes (optional)" v={payload.notes} on={(v) => set({ notes: v })} testid="cb-notes" rows={3} />
      </div>
    </Card>
  );
};

// ───────────────────────── Step 5 — Access ─────────────────────────
const PERMS = [
  { v: 'view', l: 'View', c: 'bg-zinc-700 text-zinc-300' },
  { v: 'edit', l: 'Edit', c: 'bg-blue-500/15 text-blue-300' },
  { v: 'upload', l: 'Upload', c: 'bg-amber-500/15 text-amber-300' },
  { v: 'approve', l: 'Approve', c: 'bg-emerald-500/15 text-emerald-300' },
];
const StepAccessPermissions = ({ payload, setRoot, stakeholders }) => {
  const setGrants = (grants) => setRoot(p => ({ ...p, access_permissions: { ...p.access_permissions, grants } }));
  const upsertGrant = (sid, perm) => {
    const idx = (payload.grants || []).findIndex(g => g.stakeholder_id === sid);
    const next = [...(payload.grants || [])];
    if (idx >= 0) next[idx] = { ...next[idx], permission_level: perm };
    else next.push({ stakeholder_id: sid, project_ids: [], permission_level: perm });
    setGrants(next);
  };
  const getPerm = (sid) => (payload.grants || []).find(g => g.stakeholder_id === sid)?.permission_level;
  return (
    <Card title="5. Access & Permissions" sub="Decide who sees what on this project" testid="step-access">
      <p className="text-[10px] text-zinc-500 mb-3">
        The <span className="text-amber-300">Primary</span> stakeholder always gets <span className="text-emerald-300">Approve</span>-level access automatically.
        For any other stakeholder, pick a permission level — they'll be promoted to a full login automatically when you create the client.
      </p>
      <div className="space-y-1.5">
        {stakeholders.length === 0 && <p className="text-[11px] text-zinc-600 italic">No stakeholders yet — go back to Step 2.</p>}
        {stakeholders.map(s => {
          const perm = getPerm(s.id);
          return (
            <div key={s.id} className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2" data-testid={`access-row-${s.id}`}>
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-[9px] text-white">{s.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-zinc-200">{s.name} {s.is_primary && <span className="text-[8px] text-amber-300 ml-1">PRIMARY</span>}</p>
                <p className="text-[9px] text-zinc-600 truncate">{s.email} · {s.title}</p>
              </div>
              {s.is_primary ? (
                <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300">Approve · Auto</span>
              ) : (
                <div className="flex gap-1">
                  <button onClick={() => upsertGrant(s.id, null)} data-testid={`perm-${s.id}-none`}
                    className={`text-[9px] px-2 py-1 rounded ${!perm ? 'bg-zinc-700 text-zinc-200' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}>No access</button>
                  {PERMS.map(p => (
                    <button key={p.v} onClick={() => upsertGrant(s.id, p.v)} data-testid={`perm-${s.id}-${p.v}`}
                      className={`text-[9px] px-2 py-1 rounded ${perm === p.v ? p.c : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}>
                      {p.l}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// ───────────────────────── Step 6 — Review ─────────────────────────
const StepReview = ({ payload, progress, onCommit, submitting, error }) => {
  const ci = payload.client_information;
  const primary = payload.stakeholders.find(s => s.is_primary);
  return (
    <Card title="6. Review & Confirm" sub="Final check before we provision the client" testid="step-review">
      <div className="grid grid-cols-2 gap-3">
        <ReviewBlock title="Client" rows={[
          ['Type', ci.client_type], ['Company', ci.company_name],
          ['Country / TZ', `${ci.country} · ${ci.timezone}`],
          ['Currency', ci.currency], ['Billing', ci.billing_type],
          ['NDA', ci.nda_status],
        ]} />
        <ReviewBlock title="Project" rows={[
          ['Name', payload.project_details.name],
          ['Deadline', payload.project_details.deadline],
          ['Type', payload.project_details.type],
          ['FPS · Res', `${payload.project_details.fps} · ${payload.project_details.resolution}`],
          ['Duration', `${payload.project_details.duration_min} min`],
          ['Budget', `${payload.project_details.currency} ${payload.project_details.budget}`],
        ]} />
        <ReviewBlock title="Stakeholders" rows={[
          ['Total', payload.stakeholders.length],
          ['Primary (login)', primary ? `${primary.name} · ${primary.email}` : '— not set —'],
          ['CC contacts', payload.stakeholders.length - (primary ? 1 : 0)],
        ]} />
        <ReviewBlock title="Brief & Access" rows={[
          ['Brief files', (payload.creative_brief.files || []).length],
          ['Brief notes', payload.creative_brief.notes ? `${payload.creative_brief.notes.length} chars` : 'none'],
          ['Explicit grants', (payload.access_permissions.grants || []).length],
        ]} />
      </div>
      <div className="mt-3 p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
        <p className="text-[10px] text-zinc-500 mb-1">Wizard completion</p>
        <div className="h-1.5 bg-zinc-800 rounded">
          <div className="h-1.5 bg-gradient-to-r from-violet-500 to-emerald-500 rounded" style={{ width: `${progress.percent}%` }} />
        </div>
        <p className="text-[10px] text-zinc-400 mt-1">{progress.percent}% — {progress.steps_completed.length} / 5 steps complete</p>
      </div>
      <button onClick={onCommit} disabled={submitting || !primary || !ci.company_name || !payload.project_details.name}
        data-testid="review-finalize-btn"
        className="w-full mt-3 px-4 py-3 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-400 hover:to-pink-400 disabled:opacity-30 rounded-sm text-sm text-white font-semibold flex items-center justify-center gap-2">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        Create Client & Project
      </button>
      {error && <p className="text-[11px] text-rose-400 mt-2 text-center">{error}</p>}
    </Card>
  );
};

const ReviewBlock = ({ title, rows }) => (
  <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
    <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-2">{title}</p>
    <table className="w-full text-[11px]">
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} className="border-b border-zinc-800/40 last:border-0">
            <td className="py-1 text-zinc-500 w-1/2">{k}</td>
            <td className="py-1 text-zinc-200">{v || <span className="text-zinc-600">—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ───────────────────────── Final Success ─────────────────────────
const FinalSuccess = ({ result, onReset }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(result.share_block); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="max-w-3xl mx-auto py-12 space-y-4" data-testid="onboarding-success">
      <div className="text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-7 h-7 text-emerald-400" />
        </div>
        <h1 className="text-2xl font-heading font-bold text-white">Client onboarded</h1>
        <p className="text-xs text-zinc-500 mt-1">{result.stakeholders_count} stakeholders · {result.contacts_count} CC contacts · {result.grants_count} access grants</p>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
        <p className="text-[10px] text-zinc-500 uppercase mb-2">Primary credentials</p>
        <div className="space-y-1 text-[12px]">
          <p><span className="text-zinc-500">Email: </span><span className="text-white font-mono">{result.primary.email}</span></p>
          <p><span className="text-zinc-500">Password: </span><span className="text-amber-300 font-mono">{result.primary.temp_password}</span></p>
          <p><span className="text-zinc-500">Login: </span><a href={result.primary.login_url} className="text-cyan-400 underline">{result.primary.login_url}</a></p>
        </div>
        {result.extra_logins?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-zinc-800">
            <p className="text-[10px] text-zinc-500 uppercase mb-2">Additional logins promoted (Step 5 access)</p>
            {result.extra_logins.map((l, i) => (
              <p key={i} className="text-[11px]"><span className="text-zinc-300">{l.name}</span> · <span className="text-zinc-500 font-mono">{l.email}</span> · <span className="text-amber-300 font-mono">{l.temp_password}</span></p>
            ))}
          </div>
        )}
        <pre className="mt-4 p-3 bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-zinc-300 whitespace-pre-wrap">{result.share_block}</pre>
        <div className="flex gap-2 mt-3">
          <button onClick={copy} className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-sm text-[11px] text-white" data-testid="copy-share-block">
            <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy share block'}
          </button>
          <button onClick={onReset} className="px-3 py-1.5 bg-violet-500 hover:bg-violet-400 rounded-sm text-[11px] text-white">Onboard another</button>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────── Right rail / Summary ─────────────────────────
const SummaryRail = ({ payload, progress }) => {
  const ci = payload.client_information;
  const primary = payload.stakeholders.find(s => s.is_primary);
  return (
    <aside className="space-y-3" data-testid="summary-rail">
      <Card title="Onboarding Summary" sub={null} compact>
        <SumRow k="Client Type" v={ci.client_type} />
        <SumRow k="Company" v={ci.company_name} />
        <SumRow k="Timezone" v={ci.timezone} />
        <SumRow k="Currency" v={ci.currency} />
        <SumRow k="Stakeholders" v={`${payload.stakeholders.length} added`} />
        <SumRow k="Primary" v={primary ? primary.name : 'not set'} />
        <SumRow k="Project" v={payload.project_details.name || 'not set'} />
        <SumRow k="Brief" v={`${(payload.creative_brief.files || []).length} files`} />
        <SumRow k="NDA" v={ci.nda_status} statusColor={ci.nda_status === 'signed' ? 'text-emerald-400' : ci.nda_status === 'pending' ? 'text-amber-400' : 'text-zinc-400'} />
      </Card>
      <Card title="Why we need this" sub={null} compact>
        <Reason text="Ensures the right people are in the loop" />
        <Reason text="Automates email notifications" />
        <Reason text="Prevents communication gaps" />
        <Reason text="Speeds up approvals & delivery" />
      </Card>
      <div className="bg-violet-500/10 border border-violet-500/30 rounded-sm p-3 text-center">
        <HelpCircle className="w-4 h-4 text-violet-300 mx-auto mb-1" />
        <p className="text-[11px] text-zinc-200 font-medium">Need help?</p>
        <p className="text-[10px] text-zinc-500 mb-2">Our support team can help with onboarding</p>
        <button className="text-[10px] px-3 py-1.5 bg-violet-500/20 border border-violet-500/40 rounded-sm text-violet-200 hover:bg-violet-500/30" data-testid="contact-support">
          Contact Support
        </button>
      </div>
    </aside>
  );
};

const SumRow = ({ k, v, statusColor }) => (
  <div className="flex justify-between py-1 text-[10px] border-b border-zinc-800/40 last:border-0">
    <span className="text-zinc-500">{k}</span>
    <span className={`${statusColor || 'text-zinc-200'} text-right max-w-[60%] truncate`}>{v || '—'}</span>
  </div>
);
const Reason = ({ text }) => (
  <div className="flex items-start gap-1.5 py-0.5">
    <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" />
    <span className="text-[10px] text-zinc-300">{text}</span>
  </div>
);

// ───────────────────────── Generic helpers ─────────────────────────
const Card = ({ title, sub, children, action, compact, testid }) => (
  <section className={`bg-zinc-900 border border-zinc-800 rounded-sm ${compact ? 'p-3' : 'p-4'}`} data-testid={testid}>
    {(title || action) && (
      <div className="flex items-start justify-between mb-3">
        {title && (
          <div>
            <h3 className={`font-heading font-semibold text-white ${compact ? 'text-[10px] uppercase tracking-wider text-zinc-400' : 'text-sm'}`}>{title}</h3>
            {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
          </div>
        )}
        {action}
      </div>
    )}
    <div className={compact ? 'space-y-0' : 'space-y-3'}>{children}</div>
  </section>
);
const Grid = ({ children, cols = 2 }) => <div className={`grid grid-cols-${cols} gap-3`}>{children}</div>;
const Label = ({ children }) => <label className="text-[9px] text-zinc-500 uppercase tracking-wider">{children}</label>;
const Input = ({ label, v, on, type = 'text', testid }) => (
  <div>
    <Label>{label}</Label>
    <input value={v ?? ''} onChange={(e) => on(e.target.value)} type={type} data-testid={testid}
      className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-violet-500/50" />
  </div>
);
const Select = ({ label, v, on, options, testid }) => (
  <div>
    <Label>{label}</Label>
    <select value={v ?? ''} onChange={(e) => on(e.target.value)} data-testid={testid}
      className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-violet-500/50">
      <option value="">—</option>
      {options.map(o => <option key={o} value={o}>{(o + '').replace(/_/g, ' ')}</option>)}
    </select>
  </div>
);
const TextArea = ({ label, v, on, rows = 2, testid }) => (
  <div>
    <Label>{label}</Label>
    <textarea value={v ?? ''} onChange={(e) => on(e.target.value)} rows={rows} data-testid={testid}
      className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-violet-500/50 resize-none" />
  </div>
);
const DraftSavedHint = ({ saving, savedAt }) => {
  if (saving) return <span className="text-[10px] text-zinc-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving draft…</span>;
  if (savedAt) return <span className="text-[10px] text-emerald-400 flex items-center gap-1"><Save className="w-3 h-3" /> Draft saved</span>;
  return null;
};
